import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardHeader, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Users, Plus, Edit, Trash2, Eye, EyeOff, Shield, 
  CheckCircle, XCircle, ArrowLeft, User, Mail, Key
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'viewer',
    can_view_dashboard: true,
    can_add_customer: false,
    can_edit_customer: false,
    can_delete_customer: false,
    can_refresh_status: true,
    can_manage_users: false,
    can_access_tickets: false,
    can_view_alerts: false,
    can_view_analytics: false,
    can_view_quality_metrics: false,
    can_view_topology: false,
    can_view_diagnostics: false,
    can_view_performance_map: false,
    has_all_customers: true,
    assigned_customer_ids: []
  });

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/monitoring-users/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('monitoringAdminToken');
      const response = await fetch(`${API_URL}/api/customer-monitoring/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleSaveUser = async () => {
    const token = localStorage.getItem('monitoringAdminToken');
    setLoading(true);
    
    try {
      const url = editingUser
        ? `${API_URL}/api/monitoring-users/${editingUser.id}`
        : `${API_URL}/api/monitoring-users/create`;
      const method = editingUser ? 'PUT' : 'POST';
      
      const submitData = { ...formData };
      if (editingUser && !formData.password) {
        delete submitData.password;
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });
      
      if (response.ok) {
        toast.success(editingUser ? 'User updated!' : 'User created!');
        setShowAddModal(false);
        setEditingUser(null);
        resetForm();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save user');
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    const token = localStorage.getItem('monitoringAdminToken');
    try {
      const response = await fetch(`${API_URL}/api/monitoring-users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('User deleted!');
        fetchUsers();
      } else {
        toast.error('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    const token = localStorage.getItem('monitoringAdminToken');
    try {
      const response = await fetch(`${API_URL}/api/monitoring-users/${userId}/toggle-status?is_active=${!currentStatus}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'}!`);
        fetchUsers();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Failed to update status');
    }
  };

  const editUser = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      can_view_dashboard: user.can_view_dashboard,
      can_add_customer: user.can_add_customer,
      can_edit_customer: user.can_edit_customer,
      can_delete_customer: user.can_delete_customer,
      can_refresh_status: user.can_refresh_status,
      can_manage_users: user.can_manage_users,
      can_access_tickets: user.can_access_tickets || false,
      can_view_alerts: user.can_view_alerts || false,
      can_view_analytics: user.can_view_analytics || false,
      can_view_quality_metrics: user.can_view_quality_metrics || false,
      can_view_topology: user.can_view_topology || false,
      can_view_diagnostics: user.can_view_diagnostics || false,
      can_view_performance_map: user.can_view_performance_map || false,
      has_all_customers: user.has_all_customers,
      assigned_customer_ids: user.assigned_customer_ids || []
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      role: 'viewer',
      can_view_dashboard: true,
      can_add_customer: false,
      can_edit_customer: false,
      can_delete_customer: false,
      can_refresh_status: true,
      can_manage_users: false,
    can_access_tickets: false,
      can_view_alerts: false,
      can_view_analytics: false,
      can_view_quality_metrics: false,
      can_view_topology: false,
      can_view_diagnostics: false,
      can_view_performance_map: false,
      has_all_customers: true,
      assigned_customer_ids: []
    });
  };

  const applyRolePreset = (role) => {
    const presets = {
      admin: {
        can_view_dashboard: true,
        can_add_customer: true,
        can_edit_customer: true,
        can_delete_customer: true,
        can_refresh_status: true,
        can_manage_users: true,
        can_view_alerts: true,
        can_view_analytics: true,
        can_view_quality_metrics: true,
        can_view_topology: true,
        can_view_diagnostics: true,
        can_view_performance_map: true,
        has_all_customers: true
      },
      operator: {
        can_view_dashboard: true,
        can_add_customer: false,
        can_edit_customer: true,
        can_delete_customer: false,
        can_refresh_status: true,
        can_manage_users: false,
    can_access_tickets: false,
        can_view_alerts: false,
        can_view_analytics: false,
        can_view_quality_metrics: false,
        can_view_topology: false,
        can_view_diagnostics: false,
        can_view_performance_map: false,
        has_all_customers: true
      },
      viewer: {
        can_view_dashboard: true,
        can_add_customer: false,
        can_edit_customer: false,
        can_delete_customer: false,
        can_refresh_status: true,
        can_manage_users: false,
    can_access_tickets: false,
        can_view_alerts: false,
        can_view_analytics: false,
        can_view_quality_metrics: false,
        can_view_topology: false,
        can_view_diagnostics: false,
        can_view_performance_map: false,
        has_all_customers: true
      }
    };
    
    setFormData({ ...formData, role, ...presets[role] });
  };

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-purple-100 text-purple-800',
      operator: 'bg-blue-100 text-blue-800',
      viewer: 'bg-gray-100 text-gray-800',
      custom: 'bg-orange-100 text-orange-800'
    };
    return colors[role] || colors.viewer;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/monitoring-dashboard')}
              variant="outline"
              className="text-white border-white hover:bg-white/20"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            <Users className="h-6 w-6" />
            <h1 className="text-xl font-bold">User Management</h1>
          </div>
          <Button
            onClick={() => {
              setEditingUser(null);
              resetForm();
              setShowAddModal(true);
            }}
            className="bg-white text-blue-600 hover:bg-blue-50"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Sub-User
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold">{users.length}</p>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active Users</p>
                  <p className="text-2xl font-bold text-green-600">
                    {users.filter(u => u.is_active).length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Inactive Users</p>
                  <p className="text-2xl font-bold text-red-600">
                    {users.filter(u => !u.is_active).length}
                  </p>
                </div>
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Admins</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {users.filter(u => u.role === 'admin').length}
                  </p>
                </div>
                <Shield className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {users.map((user) => (
            <Card key={user.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      user.is_active ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <User className={`h-6 w-6 ${user.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900">{user.username}</h3>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getRoleBadge(user.role)}`}>
                    {user.role.toUpperCase()}
                  </span>
                </div>

                {/* Status */}
                <div className="mb-3">
                  <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${
                    user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.is_active ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    {user.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>

                {/* Permissions Summary */}
                <div className="mb-3 space-y-1">
                  <p className="text-xs font-medium text-gray-700">Permissions:</p>
                  <div className="flex flex-wrap gap-1">
                    {user.can_add_customer && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">Add</span>}
                    {user.can_edit_customer && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">Edit</span>}
                    {user.can_delete_customer && <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">Delete</span>}
                    {user.can_manage_users && <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">Manage Users</span>}
                    {!user.has_all_customers && (
                      <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs">
                        Limited Access ({user.assigned_customer_ids?.length || 0} customers)
                      </span>
                    )}
                  </div>
                </div>

                {/* Last Login */}
                {user.last_login && (
                  <p className="text-xs text-gray-500 mb-3">
                    Last login: {new Date(user.last_login).toLocaleDateString()}
                  </p>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-3 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => editUser(user)}
                    className="flex-1"
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleUserStatus(user.id, user.is_active)}
                    className={user.is_active ? 'text-red-600' : 'text-green-600'}
                  >
                    {user.is_active ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(user.id)}
                    className="text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {users.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Users className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No sub-users yet</h3>
              <p className="text-gray-500 mb-4">Create sub-users to manage access to the monitoring dashboard</p>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add First Sub-User
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 pb-3 border-b flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              {editingUser ? 'Edit User' : 'Create New Sub-User'}
            </h2>
            
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Basic Information
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Username *</label>
                  <Input
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="johndoe"
                    disabled={editingUser}
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="user@example.com"
                    className="w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Password {editingUser ? '(Leave empty to keep current)' : '*'}
                  </label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter password"
                      className="w-full pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Role Selection */}
              <div className="space-y-3 pt-3 border-t">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Role & Permissions
                </h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Quick Role Preset</label>
                  <div className="grid grid-cols-3 gap-2">
                    {['admin', 'operator', 'viewer'].map((role) => (
                      <button
                        key={role}
                        type="button"
                        onClick={() => applyRolePreset(role)}
                        className={`px-4 py-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                          formData.role === role
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Permissions */}
                <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                  <p className="text-xs font-medium text-gray-700 mb-2">Custom Permissions</p>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_view_dashboard}
                      onChange={(e) => setFormData({ ...formData, can_view_dashboard: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">View Dashboard</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_add_customer}
                      onChange={(e) => setFormData({ ...formData, can_add_customer: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Add Customer</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_edit_customer}
                      onChange={(e) => setFormData({ ...formData, can_edit_customer: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Edit Customer</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_delete_customer}
                      onChange={(e) => setFormData({ ...formData, can_delete_customer: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Delete Customer</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_refresh_status}
                      onChange={(e) => setFormData({ ...formData, can_refresh_status: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Refresh Status</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_manage_users}
                      onChange={(e) => setFormData({ ...formData, can_manage_users: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-purple-700 font-medium">Manage Sub-Users (Admin only)</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_access_tickets || false}
                      onChange={(e) => setFormData({ ...formData, can_access_tickets: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-orange-700 font-medium">🎫 Access Ticket Dashboard</span>
                  </label>
                </div>

                {/* Advanced Features Permissions */}
                <div className="bg-blue-50 p-3 rounded-lg space-y-2 mt-3">
                  <p className="text-xs font-semibold text-blue-800 mb-2">Advanced Features (Admin by default)</p>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_view_alerts}
                      onChange={(e) => setFormData({ ...formData, can_view_alerts: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">View Alerts & Notifications</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_view_analytics}
                      onChange={(e) => setFormData({ ...formData, can_view_analytics: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">View Analytics Dashboard</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_view_quality_metrics}
                      onChange={(e) => setFormData({ ...formData, can_view_quality_metrics: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">View Network Quality Metrics</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_view_topology}
                      onChange={(e) => setFormData({ ...formData, can_view_topology: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">View Network Topology</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_view_diagnostics}
                      onChange={(e) => setFormData({ ...formData, can_view_diagnostics: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">View Network Diagnostics</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.can_view_performance_map}
                      onChange={(e) => setFormData({ ...formData, can_view_performance_map: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">View Performance Map</span>
                  </label>
                </div>
              </div>

              {/* Customer Access */}
              <div className="space-y-3 pt-3 border-t">
                <h3 className="text-sm font-semibold text-gray-700">Customer Access</h3>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_all_customers}
                    onChange={(e) => setFormData({ ...formData, has_all_customers: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Access to All Customers</span>
                </label>

                {!formData.has_all_customers && (
                  <div className="bg-orange-50 p-3 rounded-lg">
                    <p className="text-xs font-medium text-orange-900 mb-2">Select Customers to Grant Access</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {customers.map((customer) => (
                        <label key={customer.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.assigned_customer_ids.includes(customer.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFormData({
                                  ...formData,
                                  assigned_customer_ids: [...formData.assigned_customer_ids, customer.id]
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  assigned_customer_ids: formData.assigned_customer_ids.filter(id => id !== customer.id)
                                });
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-xs">{customer.office_name} - {customer.static_ip}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 mt-6 pt-4 border-t">
              <Button
                onClick={() => {
                  setShowAddModal(false);
                  setEditingUser(null);
                  resetForm();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveUser}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600"
              >
                {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
